import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { HomeIcon, HeartIcon, SparklesIcon, MapPinIcon } from '@heroicons/react/24/outline';
import { useAnalyticsWithConsent } from '../../hooks/useAnalyticsWithConsent';
import { useAuth } from '../../auth/firebaseAuth';

interface CategoryCard {
  title: string;
  description: string;
  path: string;
  image: string;
  icon: React.ComponentType<any>;
  badge?: string;
  location: string;
}

const categories: CategoryCard[] = [
  {
    title: 'Affordable Rentals in Dublin',
    description: 'Budget-friendly apartments and houses under €850/month',
    path: '/affordable-rentals-dublin',
    image: 'https://images.unsplash.com/photo-1609342122563-a43ac8917a3a?w=800&h=600&fit=crop',
    icon: HomeIcon,
    badge: 'Popular',
    location: 'Dublin',
  },
  {
    title: 'Family Homes in Limerick',
    description: 'Spacious houses perfect for growing families',
    path: '/family-homes-limerick',
    image: 'https://images.unsplash.com/photo-1570129477492-45c003edd2be?w=800&h=600&fit=crop',
    icon: HeartIcon,
    location: 'Limerick',
  },
  {
    title: 'Energy-Efficient Homes in Dublin',
    description: 'Eco-friendly A-rated BER properties',
    path: '/energy-efficient-homes-dublin',
    image: 'https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=800&h=600&fit=crop',
    icon: SparklesIcon,
    location: 'Dublin',
  },
  {
    title: 'Affordable House Shares in Cork',
    description: 'Budget-friendly co-living under €600/month',
    path: '/affordable-house-shares-cork',
    image: 'https://images.unsplash.com/photo-1502672260266-1c1ef2d93688?w=800&h=600&fit=crop',
    icon: HomeIcon,
    location: 'Cork',
  },
];

const ExploreCategoriesPage: React.FC = () => {
  const analytics = useAnalyticsWithConsent();
  const { currentUser } = useAuth();

  useEffect(() => {
    analytics.trackPixelPageView({
      email: currentUser?.email || undefined,
      externalId: currentUser?.uid,
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [analytics, currentUser]);

  const handleCategoryClick = (category: CategoryCard) => {
    analytics.logEvent('search', {
      search_term: category.title,
      category_path: category.path,
      category_location: category.location,
      has_badge: !!category.badge,
    });
  };

  return (
    <>
      <Helmet>
        <title>RentSmart: Properties for Rent in Ireland | Explore Categories</title>
        <meta
          name="description"
          content="Browse curated property categories across Ireland. Find affordable rentals, luxury homes, student accommodation, family houses, and more in Dublin, Cork, Galway, and beyond."
        />
        <meta
          name="keywords"
          content="property categories, rental properties Ireland, homes for sale Ireland, Dublin rentals, Cork properties, Galway apartments, affordable housing, luxury homes, student accommodation"
        />
        <link rel="canonical" href="https://rentsmart.ie/explore-categories" />
        <meta property="og:title" content="Explore Property Categories | RentSmart.ie" />
        <meta
          property="og:description"
          content="Discover your ideal property from our curated categories. From affordable rentals to luxury homes across Ireland's major cities."
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://rentsmart.ie/explore-categories" />
      </Helmet>

      <div className="relative min-h-screen w-screen max-w-none left-1/2 right-1/2 ml-[-50vw] mr-[-50vw] overflow-visible bg-gradient-to-b from-indigo-50 to-white py-6 sm:py-8 md:py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header Section */}
          <div className="text-center mb-8 sm:mb-10 md:mb-12">
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-extrabold text-indigo-900 mb-3 sm:mb-4">
              Explore Rental Categories: Find Properties for Rent in Ireland
            </h1>
            <p className="text-base sm:text-lg md:text-xl text-gray-600 max-w-3xl mx-auto px-4">
              Discover your perfect home from our curated selection of properties across Ireland.
              From affordable rentals to luxury estates, find exactly what you're looking for.
            </p>
          </div>

          {/* Categories Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 sm:gap-6 md:gap-8">
            {categories.map((category) => {
              const Icon = category.icon;
              return (
                <Link
                  key={category.path}
                  to={category.path}
                  onClick={() => handleCategoryClick(category)}
                  className="group relative bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 no-underline"
                >
                  {/* Image */}
                  <div className="relative h-40 sm:h-48 md:h-56 overflow-hidden">
                    <img
                      src={category.image}
                      alt={category.title}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                      loading="lazy"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>

                    {/* Badge */}
                    {category.badge && (
                      <div className="absolute top-3 right-3 bg-indigo-600 text-white px-2.5 sm:px-3 py-1 rounded-full text-xs sm:text-sm font-semibold">
                        {category.badge}
                      </div>
                    )}

                    {/* Location */}
                    <div className="absolute bottom-3 left-3 flex items-center text-white">
                      <MapPinIcon className="h-4 w-4 sm:h-5 sm:w-5 mr-1" />
                      <span className="text-xs sm:text-sm font-medium">{category.location}</span>
                    </div>
                  </div>

                  {/* Content */}
                  <div className="p-4 sm:p-5 md:p-6">
                    <div className="flex items-start mb-2 sm:mb-3">
                      <Icon className="h-5 w-5 sm:h-6 sm:w-6 text-indigo-600 mr-2 sm:mr-3 flex-shrink-0 mt-0.5" />
                      <h3 className="text-base sm:text-lg md:text-xl font-bold text-gray-900 group-hover:text-indigo-600 transition-colors">
                        {category.title}
                      </h3>
                    </div>
                    <p className="text-xs sm:text-sm md:text-base text-gray-600 leading-relaxed">
                      {category.description}
                    </p>

                    {/* CTA */}
                    <div className="mt-3 sm:mt-4 flex items-center text-indigo-600 font-medium text-xs sm:text-sm">
                      <span>Browse Properties</span>
                      <svg
                        className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9 5l7 7-7 7"
                        />
                      </svg>
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>

          {/* CTA Section */}
          <div className="mt-12 sm:mt-16 md:mt-20 bg-indigo-600 rounded-2xl shadow-2xl p-6 sm:p-8 md:p-12 text-center">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-3 sm:mb-4">
              Can't Find What You're Looking For?
            </h2>
            <p className="text-base sm:text-lg md:text-xl text-indigo-100 mb-6 sm:mb-8 max-w-2xl mx-auto">
              Use our advanced search to filter properties by your exact preferences.
            </p>
            <Link
              to="/browse"
              className="inline-block bg-white text-indigo-600 px-6 py-3 sm:px-8 sm:py-4 rounded-lg font-semibold text-sm sm:text-base md:text-lg hover:bg-indigo-50 transition-colors duration-300 shadow-lg no-underline"
            >
              Advanced Search
            </Link>
          </div>
        </div>
      </div>
    </>
  );
};

export default ExploreCategoriesPage;
